import { OperatingConditions } from 'src/components/OperatingConditions';
import PTPEquipmentPanel from '../PTPEquipmentPanel';
import {
  PTPChoices,
  PTPFormPanel,
  PTPPanelConfigImpl,
  PTPShowProps,
  syncFormState,
  getEndFromChoices,
  getEquipmentFromChoices,
} from '../utils';
import {
  genericEndPerformanceSummary,
  genericLinkSummary,
  genericEndConfig,
  complexPolarizationConfig,
  multibandEquipmentGetters,
  syncMultibandHi,
} from './config-utils';
import additionalMessages from 'src/messages';

const equipment: PTPFormPanel<PTPShowProps> = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'equipment_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'band',
      getter: 'local.radios.0.equipment.band',
      ...multibandEquipmentGetters('local.radios.0.equipment.band'),
    },
    {
      attrName: 'product',
      getter: 'local.radios.0.equipment.product',
      ...multibandEquipmentGetters('local.radios.0.equipment.product'),
    },
    {
      attrName: 'regulation',
      getter: 'local.radios.0.equipment.regulation',
    },
    {
      attrName: 'aes_encryption',
      getter: 'local.radios.0.equipment.aes_encryption',
      ...multibandEquipmentGetters('local.radios.0.equipment.aes_encryption'),
    },
    {
      attrName: 'link_protection',
      getter: 'local.radios.0.equipment.link_protection',
      afterOnChange(newValue, { getValues, setValue }) {
        // reset frequencies whenever link kind changes
        const radios = getValues('local.radios');
        for (let i = 0; i < radios.length; i++) {
          // hack: copy across paths to prevent frequency endpoint failing
          // (changing from 4+0 to 1+1 didn't work - not sure why it works
          // for other link kinds)
          setValue(`local.radios.${i}.equipment.link_protection`, newValue);
          setValue(`remote.radios.${i}.equipment.link_protection`, newValue);
          // reset freqs
          setValue(`local.radios.${i}.frequency.tx_frequency`, null, {
            shouldDirty: true,
          });
          setValue(`remote.radios.${i}.frequency.tx_frequency`, null, {
            shouldDirty: true,
          });
        }
      },
    },
    /* Temporarily disable path aggregation
    {
      attrName: 'path_aggregation',
      getter: 'local.radios.0.equipment.path_aggregation',
    },
    */
    {
      attrName: 'remote_mount',
      getter: 'local.radios.0.equipment.remote_mount',
      ...multibandEquipmentGetters('local.radios.0.equipment.remote_mount'),
    },
    {
      attrName: 'ethernet_configuration',
      getter: 'local.radios.0.equipment.ethernet_configuration',
      ...multibandEquipmentGetters(
        'local.radios.0.equipment.ethernet_configuration'
      ),
    },
    {
      attrName: 'ethernet_interface_protection',
      getter: 'local.radios.0.equipment.ethernet_interface_protection',
      show({ choices }) {
        const eq = getEquipmentFromChoices(choices);
        return eq.link_protection?.value === 'unit_redundancy';
      },
    },
  ],
};

function getSummaryOperatingConditions(): PTPFormPanel<any> {
  return {
    name: 'summary_operating_conditions',
    title: additionalMessages.operatingConditions,
    show: ({ choices }: { choices: PTPChoices }) => {
      return getEndFromChoices(choices, 'local').supports_frame_size ?? false;
    },
    component: OperatingConditions,
    fields: [
      {
        label: 'Frame Size',
        getter: 'summary.0.link.frame_size',
        choicesGetter: 'local.end.frame_size_choices',
        valueGetter: 'local.end.default_frame_size',
        units: 'Bytes',
        lk: {
          choicesGetter: 'paths.0.local.end.frame_size_choices',
          valueGetter: 'paths.0.local.end.default_frame_size',
        },
        nextValue(currentValue: string, newChoices: PTPChoices) {
          const endChoices = getEndFromChoices(newChoices, 'local');
          const choices = endChoices.frame_size_choices;
          if (choices != null) {
            if (!choices.includes(currentValue)) {
              return endChoices.default_frame_size;
            }
          }
          return null;
        },
      },
    ],
  };
}

const config: PTPFormPanel<any> = {
  kind: 'config',
  title: 'Product Configuration',
  name: 'equipment_config_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'tr_spacing',
      getter: 'local.radios.0.equipment.tr_spacing',
      ...multibandEquipmentGetters('local.radios.0.equipment.tr_spacing'),
    },
    {
      attrName: 'bandwidth',
      getter: 'local.radios.0.equipment.bandwidth',
      ...multibandEquipmentGetters('local.radios.0.equipment.bandwidth'),
    },
    {
      attrName: 'modulation_mode',
      getter: 'local.radios.0.equipment.modulation_mode',
      units: 'ms',
      ...multibandEquipmentGetters('local.radios.0.equipment.modulation_mode'),
    },
    {
      attrName: 'acmb',
      getter: 'local.radios.0.equipment.acmb',
      ...multibandEquipmentGetters('local.radios.0.equipment.acmb'),
    },
    {
      attrName: 'max_mod_mode',
      getter: 'local.radios.0.equipment.max_mod_mode',
      ...multibandEquipmentGetters('local.radios.0.equipment.max_mod_mode'),
    },
    {
      attrName: 'min_mod_mode',
      getter: 'local.radios.0.equipment.min_mod_mode',
      ...multibandEquipmentGetters('local.radios.0.equipment.min_mod_mode'),
    },
    complexPolarizationConfig(),
    {
      attrName: 'atpc',
      getter: 'local.radios.0.equipment.atpc',
      ...multibandEquipmentGetters('local.radios.0.equipment.atpc'),
    },
    {
      attrName: 'hi',
      getter: 'local.radios.0.equipment.hi',
      ...multibandEquipmentGetters('local.radios.0.equipment.hi', {
        sync: syncMultibandHi,
      }),
    },
    {
      attrName: 'header_compression',
      getter: 'local.radios.0.equipment.header_compression',
      ...multibandEquipmentGetters('local.radios.0.equipment.header_compression'),
    },
  ],
};

const tdm: PTPFormPanel<any> = {
  kind: 'tdm',
  title: 'TDM Configuration',
  name: 'tdm_config_link_a',
  component: PTPEquipmentPanel,
  show({ choices }) {
    const eq = getEquipmentFromChoices(choices);
    if (!eq?.product) return false;
    const productValue = eq.product.value;
    const hasTdm =
      productValue.startsWith('PTP 820G') ||
      productValue.startsWith('PTP 820A');
    if (!hasTdm) return false;
    // Hide TDM for 1+0 SD (Internal BBC) as it does not support line cards
    const lk = eq.link_protection?.value;
    if (lk === 'sd_1plus0') return false;
    return true;
  },
  fields: [
    {
      attrName: 'tdm_type',
      getter: 'local.radios.0.equipment.tdm_type',
    },
    {
      attrName: 'licensed_tdm',
      getter: 'local.radios.0.equipment.licensed_tdm',
      lk: {
        '2plus0': {
          getter: [
            'local.radios.0.equipment.licensed_tdm',
            'local.radios.1.equipment.licensed_tdm',
          ],
          separate: ['co_polar', 'cross_polar', 'xpic'],
          sync(getter, setter, currentIdx, newValue) {
            const otherIdx = currentIdx === 1 ? 0 : 1;
            const path = `local.radios.${otherIdx}.equipment.licensed_tdm`;
            const otherValue = getter(path);
            if (otherValue == null) {
              setter(path, 0, { shouldDirty: true });
            }
          },
        },
        multiband: {
          getter: [
            'local.radios.0.equipment.licensed_tdm',
            'local.radios.1.equipment.licensed_tdm',
          ],
          separate: ['multi_co_polar', 'multi_cross_polar', 'multi_xpic'],
        },
        '2plus2': {
          getter: [
            'local.radios.0.equipment.licensed_tdm',
            'local.radios.1.equipment.licensed_tdm',
          ],
          separate: [
            'co_polar_2plus2',
            'cross_polar_2plus2',
            'xpic_2plus2',
            'sd_2plus2',
          ],
          sync(getter, setter, currentIdx, newValue) {
            const otherIdx = currentIdx === 1 ? 0 : 1;
            const path = `local.radios.${otherIdx}.equipment.licensed_tdm`;
            const otherValue = getter(path);
            if (otherValue == null) {
              setter(path, 0, { shouldDirty: true });
            }
          },
        },
      },
    },
  ],
};

const ptp820PanelConfig: PTPPanelConfigImpl = {
  syncFormState,
  equipment: [equipment, config, tdm],
  performance: {
    summary: {
      link: [
        getSummaryOperatingConditions(),
        genericLinkSummary({
          showAggregateThroughput: true,
          showMinPayloadCapacity: true,
          popupAttr: 'summary.@.link.availability_report',
          showTdm: true,
        }),
      ],
      local: genericEndPerformanceSummary('local'),
      remote: genericEndPerformanceSummary('remote'),
    },
  },
  ends: {
    title: 'Configuration at Each End',
    local: genericEndConfig('local', {
      powerSource: true,
      ethernetCable: true,
      supportsLicensedSD: true,
      secondaryFeederLoss: true,
    }),
    remote: genericEndConfig('remote', {
      powerSource: true,
      ethernetCable: true,
      supportsLicensedSD: true,
      secondaryFeederLoss: true,
    }),
  },
};

export default ptp820PanelConfig;
